import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
} from 'recharts'
import { getCSATDistribution } from '@/pages/api/widgetDetails'
import { useTeamContext } from '@/context/TeamContext'

interface WidgetCSATDistributionChartProps {
	widgetId: string
}

const COLORS = {
	1: '#ef4444', // red-500
	2: '#f97316', // orange-500
	3: '#f59e0b', // amber-500
	4: '#84cc16', // lime-500
	5: '#22c55e', // green-500
}

const LABELS = {
	1: 'Very Dissatisfied',
	2: 'Dissatisfied',
	3: 'Neutral',
	4: 'Satisfied',
	5: 'Very Satisfied',
}

const CustomTooltip = ({ active, payload }: any) => {
	if (active && payload && payload.length) {
		const data = payload[0].payload
		return (
			<div className="bg-popover text-popover-foreground p-2 rounded-lg shadow-lg border">
				<p className="font-medium">
					{LABELS[data.score as keyof typeof LABELS]}
				</p>
				<p className="text-sm text-muted-foreground">Count: {data.count}</p>
			</div>
		)
	}
	return null
}

const WidgetCSATDistributionChart: React.FC<
	WidgetCSATDistributionChartProps
> = ({ widgetId }) => {
	const { teamId } = useTeamContext()

	const { data, isLoading, error } = useQuery({
		queryKey: ['widgetCsatDistribution', widgetId, teamId],
		queryFn: () => getCSATDistribution(widgetId, teamId!),
		enabled: !!widgetId && !!teamId,
	})

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>CSAT Distribution</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-[300px]">
						<Skeleton className="h-full w-full" />
					</div>
				</CardContent>
			</Card>
		)
	}

	if (error) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>CSAT Distribution</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-[300px] flex items-center justify-center text-destructive">
						Error loading CSAT distribution
					</div>
				</CardContent>
			</Card>
		)
	}

	if (!data || data.total === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>CSAT Distribution</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="h-[300px] flex items-center justify-center text-muted-foreground">
						No CSAT data available for this period
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center justify-between">
					<span>CSAT Distribution</span>
					<span className="text-sm font-normal text-muted-foreground">
						Average: {data.average.toFixed(1)}/5
					</span>
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="h-[300px] w-full">
					<ResponsiveContainer width="100%" height="100%">
						<BarChart
							data={data.distribution}
							margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
							<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
							<XAxis
								dataKey="score"
								tickFormatter={(value) => `${value}`}
								className="text-xs text-muted-foreground"
							/>
							<YAxis className="text-xs text-muted-foreground" />
							<Tooltip content={<CustomTooltip />} />
							<Bar
								dataKey="count"
								fill="currentColor"
								className="fill-primary"
								radius={[4, 4, 0, 0]}
								barSize={40}
							/>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</CardContent>
		</Card>
	)
}

export default WidgetCSATDistributionChart
